import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'out-gallery',
  imports: [CommonModule],
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
  animations: [
    trigger('slideAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(20px)' }),
        animate(
          '300ms ease-in',
          style({ opacity: 1, transform: 'translateX(0)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-out',
          style({ opacity: 0, transform: 'translateX(-20px)' })
        ),
      ]),
    ]),
  ],
})
export class GalleryComponent {
  @Input() images: any[] = [];
  @Input() index = 0;
  @Output() indexChange = new EventEmitter<number>();

  @Input() open = false;
  @Output() closeEvent = new EventEmitter<void>();
  @Input() loop = false;
  @Input() noThumbs = false;
  @Input() videoUrl: string | null = null;

  slide = 'next';
  swipeDone = false;
  swipeX: number | null = null;

  constructor(private sanitizer: DomSanitizer) {}

  get prevImage(): number {
    if (this.index > 0) {
      return this.index - 1;
    } else if (this.loop && this.images && this.images.length > 0) {
      return this.images.length - 1;
    }
    return this.index;
  }

  get nextImage(): number {
    if (this.images && this.index < this.images.length - 1) {
      return this.index + 1;
    } else if (this.loop && this.images && this.images.length > 0) {
      return 0;
    }
    return this.index;
  }

  isVimeo(): boolean {
    const image = this.images[this.index];
    if (typeof image === 'string') {
      return image.includes('vimeocdn.com') || image.includes('vimeo.com');
    } else if (image && image.src) {
      return image.src.includes('vimeo.com');
    }
    return false;
  }

  getVideoUrl(): SafeResourceUrl | null {
    if (!this.videoUrl) {
      return null;
    }
    // Append autoplay (=1) and muted (=1) parameters
    let videoUrlWithParams = this.videoUrl;
    // Check if there is already a query parameter
    if (videoUrlWithParams.indexOf('?') > -1) {
      videoUrlWithParams += '&autoplay=1&muted=1';
    } else {
      videoUrlWithParams += '?autoplay=1&muted=1';
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(videoUrlWithParams);
  }

  getImageSrc(): string {
    const image = this.images[this.index];
    return typeof image === 'string' ? image : image.src || '';
  }

  getImageAlt(): string {
    const image = this.images[this.index];
    return typeof image === 'string' ? '' : image.alt || image.caption || '';
  }

  getThumbnail(image: any): string {
    return typeof image === 'string'
      ? image
      : image.thumbnail || image.src || '';
  }

  onClose() {
    this.closeEvent.emit();
  }

  prev() {
    let newIndex = this.index;
    if (this.index > 0) {
      newIndex = this.index - 1;
    } else if (this.loop && this.images.length > 0) {
      newIndex = this.images.length - 1;
    }
    this.goto(newIndex, 'prev');
  }

  next() {
    let newIndex = this.index;
    if (this.index < this.images.length - 1) {
      newIndex = this.index + 1;
    } else if (this.loop && this.images.length > 0) {
      newIndex = 0;
    }
    this.goto(newIndex, 'next');
  }

  goto(idx: number, slide?: string) {
    this.slide = slide || (this.index < idx ? 'next' : 'prev');
    this.index = idx;
    this.indexChange.emit(this.index);
  }

  swipeStart(e: TouchEvent) {
    this.swipeDone = false;
    if (e.changedTouches.length === 1) {
      this.swipeX = e.changedTouches[0].screenX;
    }
  }

  swipe(e: TouchEvent) {
    if (
      !this.swipeDone &&
      e.changedTouches.length === 1 &&
      this.swipeX !== null
    ) {
      const swipeDistance = e.changedTouches[0].screenX - this.swipeX;
      if (swipeDistance >= 50) {
        this.prev();
        this.swipeDone = true;
      } else if (swipeDistance <= -50) {
        this.next();
        this.swipeDone = true;
      }
    }
  }
}
